// ─── Dezimalzahlen ohne Gleitkomma ───────────────────────────────────────────
// Festkomma auf BigInt-Basis mit 12 Nachkommastellen. Reicht für Geld (gerundet
// wird erst an den fachlich definierten Stellen auf 2 Stellen) und für Faktoren
// wie Zielpreis-Skalierung, ohne dass 0,1 + 0,2 zu 0,30000000000000004 wird.
//
// Rundung überall kaufmännisch: ab der Hälfte vom Nullpunkt weg.

const STELLEN = 12;
const SKALA = BigInt('1' + '0'.repeat(STELLEN));
const NULL = BigInt(0);
const ZWEI = BigInt(2);

function teileGerundet(zaehler, nenner) {
  if (nenner === NULL) throw new Error('Division durch null');
  let q = zaehler / nenner;
  const rest = zaehler % nenner;
  const absRest = rest < NULL ? -rest : rest;
  const absNenner = nenner < NULL ? -nenner : nenner;
  if (absRest * ZWEI >= absNenner) {
    const negativ = (zaehler < NULL) !== (nenner < NULL);
    q += negativ ? BigInt(-1) : BigInt(1);
  }
  return q;
}

function ausText(text) {
  let s = String(text).trim().replace(/\s/g, '');
  // Deutsches Format "1.234,56" und internationales "1,234.56" erkennen
  if (s.includes(',') && s.includes('.')) {
    s = s.lastIndexOf(',') > s.lastIndexOf('.')
      ? s.replace(/\./g, '').replace(',', '.')
      : s.replace(/,/g, '');
  } else if (s.includes(',')) {
    s = s.replace(',', '.');
  }
  const m = s.match(/^([+-])?(\d*)(?:\.(\d*))?$/);
  if (!m || (m[2] === '' && (m[3] === undefined || m[3] === ''))) {
    throw new Error(`Keine gültige Zahl: "${text}"`);
  }
  const vorzeichen = m[1] === '-' ? BigInt(-1) : BigInt(1);
  const ganz = BigInt(m[2] || '0');
  const bruchRoh = (m[3] || '');
  // Mehr als 12 Nachkommastellen: kaufmännisch auf 12 runden
  let bruch;
  if (bruchRoh.length > STELLEN) {
    const lang = BigInt(bruchRoh);
    const teiler = BigInt('1' + '0'.repeat(bruchRoh.length - STELLEN));
    bruch = teileGerundet(lang, teiler);
  } else {
    bruch = BigInt(bruchRoh.padEnd(STELLEN, '0') || '0');
  }
  return vorzeichen * (ganz * SKALA + bruch);
}

export class Dez {
  constructor(roh) {
    this.roh = roh; // BigInt, skaliert mit 10^12
  }

  static von(x) {
    if (x instanceof Dez) return x;
    if (x === null || x === undefined || x === '') return new Dez(NULL);
    if (typeof x === 'bigint') return new Dez(x * SKALA);
    if (typeof x === 'number') {
      if (!Number.isFinite(x)) throw new Error(`Keine endliche Zahl: ${x}`);
      return new Dez(ausText(x.toFixed(STELLEN)));
    }
    return new Dez(ausText(x));
  }

  plus(b) { return new Dez(this.roh + Dez.von(b).roh); }
  minus(b) { return new Dez(this.roh - Dez.von(b).roh); }
  mal(b) { return new Dez(teileGerundet(this.roh * Dez.von(b).roh, SKALA)); }
  durch(b) {
    const n = Dez.von(b).roh;
    if (n === NULL) throw new Error('Division durch null');
    return new Dez(teileGerundet(this.roh * SKALA, n));
  }
  neg() { return new Dez(-this.roh); }
  abs() { return this.roh < NULL ? this.neg() : this; }

  runde(stellen = 2) {
    const teiler = BigInt('1' + '0'.repeat(STELLEN - stellen));
    return new Dez(teileGerundet(this.roh, teiler) * teiler);
  }

  vergleiche(b) {
    const o = Dez.von(b).roh;
    return this.roh < o ? -1 : this.roh > o ? 1 : 0;
  }
  istNull() { return this.roh === NULL; }
  istNegativ() { return this.roh < NULL; }
  gleich(b) { return this.vergleiche(b) === 0; }
  kleinerAls(b) { return this.vergleiche(b) < 0; }
  groesserAls(b) { return this.vergleiche(b) > 0; }

  // Für Anzeige und Speicherung — erst NACH dem Runden verwenden.
  alsText(stellen = STELLEN) {
    const r = this.runde(Math.min(stellen, STELLEN)).roh;
    const negativ = r < NULL;
    const abs = negativ ? -r : r;
    const ganz = abs / SKALA;
    let bruch = (abs % SKALA).toString().padStart(STELLEN, '0').slice(0, stellen);
    const text = stellen > 0 ? `${ganz}.${bruch}` : `${ganz}`;
    return (negativ && !/^0(\.0*)?$/.test(text) ? '-' : '') + text;
  }
  alsZahl(stellen = STELLEN) { return Number(this.alsText(stellen)); }
  toJSON() { return this.alsZahl(); }
  toString() { return this.alsText(); }
}

export const d = (x) => Dez.von(x);
export const summe = (werte) => werte.reduce((s, w) => s.plus(w), Dez.von(0));
export const runde2 = (x) => Dez.von(x).runde(2);
export const runde3 = (x) => Dez.von(x).runde(3);
